import classNames from 'classnames';
import Slider from 'rc-slider';
import { useEffect, useRef, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import styles from './range-slider.module.scss';

const KEYBOARD_CHANGE_DELAY = 500;

export interface RangeSliderProps {
  /** The accessible name of the slider, read out by screen readers */
  ariaLabel?: string;
  /** The class name to apply */
  className?: string;
  /** Indicates whether the slider is disabled */
  disabled?: boolean;
  /** Indicates whether to display the current value of the slider */
  displayValue?: boolean;
  /** A function to format the displayed value */
  formatValue?: (value: number) => string;
  /** The maximum label */
  maxLabel?: string;
  /** The maximum value for the slider */
  maxValue?: number;
  /** The minimum label */
  minLabel?: string;
  /** The minimum value for the slider */
  minValue?: number;
  /** An event that is triggered after the drag has finished */
  onChange: (value: number) => void;
  /** Whether the value can be updated externally */
  shouldUpdateValue?: boolean;
  /** The amount the slider moves when dragging */
  step?: number;
  /** Unit for the slider value, displayed to the right of the value */
  unit?: string;
  /** The current value of the slider */
  value?: number;
  /** Additional label to be displayed next to the value on top of the slider */
  valueLabel?: string;
}

const RangeSlider = ({
  ariaLabel,
  className,
  disabled = false,
  displayValue = false,
  formatValue,
  maxLabel,
  maxValue,
  minLabel,
  minValue = 0,
  onChange,
  shouldUpdateValue = false,
  step = 1,
  unit,
  value: initialValue = minValue,
  valueLabel,
}: RangeSliderProps) => {
  const [currentValue, setCurrentValue] = useState(initialValue);
  const isKeyboardRef = useRef(false);
  const pendingValueRef = useRef<number | null>(null);
  const keyboardTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleChange = (newValue: number) => {
    setCurrentValue(newValue);
  };

  const commitPendingValue = () => {
    clearTimeout(keyboardTimeoutRef.current);

    if (pendingValueRef.current === null) return;

    const pendingValue = pendingValueRef.current;

    pendingValueRef.current = null;
    onChange(pendingValue);
  };

  const handleChangeComplete = (newValue: number) => {
    if (!isKeyboardRef.current) {
      onChange(newValue);

      return;
    }

    // The slider completes a change on every key press, so keyboard changes
    // are only reported once the keys have been idle for a moment
    pendingValueRef.current = newValue;
    clearTimeout(keyboardTimeoutRef.current);
    keyboardTimeoutRef.current = setTimeout(
      commitPendingValue,
      KEYBOARD_CHANGE_DELAY,
    );
  };

  const getFormattedValue = (value: number) =>
    formatValue ? formatValue(value) : unit ? `${value} ${unit}` : `${value}`;

  useEffect(() => {
    if (!shouldUpdateValue) return;

    setCurrentValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  useEffect(() => () => clearTimeout(keyboardTimeoutRef.current), []);

  // Tracks whether the slider is used with the keyboard or a pointer, in the
  // capture phase so it's known before the slider itself handles the event
  const handleKeyDownCapture = () => {
    isKeyboardRef.current = true;
  };

  // A pending keyboard change is older than the pointer change that follows,
  // so it's reported now rather than when its delay runs out mid-drag
  const handlePointerDownCapture = () => {
    isKeyboardRef.current = false;
    commitPendingValue();
  };

  return (
    <div
      className={classNames(
        styles.component,
        {
          [styles.isDisabled]: disabled,
        },
        className,
      )}
      onKeyDownCapture={handleKeyDownCapture}
      onPointerDownCapture={handlePointerDownCapture}
    >
      {displayValue && (
        <div className={styles.topLabel}>
          <Typography tagStyle="bodyMedium">
            {valueLabel} {getFormattedValue(currentValue)}
          </Typography>
        </div>
      )}

      <Slider
        ariaLabelForHandle={ariaLabel}
        // Read out the value as it is shown, e.g. with its unit
        ariaValueTextFormatterForHandle={getFormattedValue}
        disabled={disabled}
        max={maxValue}
        min={minValue}
        // Leaving the slider reports a pending keyboard change right away
        onBlur={commitPendingValue}
        onChange={value => handleChange(value as number)}
        onChangeComplete={value => handleChangeComplete(value as number)}
        step={step}
        value={currentValue}
      />

      <div className={styles.bottomLabels}>
        <Typography tagStyle="bodyMedium">{minLabel}</Typography>

        <Typography tagStyle="bodyMedium">{maxLabel}</Typography>
      </div>
    </div>
  );
};

RangeSlider.displayName = 'RangeSlider';

export default RangeSlider;
